import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, ViewChild} from '@angular/core';
import {CoverImageComponent} from '@aviary/components/cover-image/cover-image.component';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {TableComponent} from '@aviary/components/table/table.component';
import {TableDefinition} from '@store/aviary/aviary.adapter';
import {CatalogRecord, CatalogRecordAvailabilityBranch} from '@store/catalog/catalog.types';
import {isPopulatedString, isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {CatalogActions} from '@store/catalog/catalog.actions';
import {Store} from '@ngrx/store';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {CatalogCarouselCardComponent} from '@app/catalog/carousels/carousel-card/carousel-card.component';
import {CatalogHomeBranchAvailabilityComponent} from '@app/catalog/bib-details/availability/catalog-home-branch-availability.component';
import {CatalogHoldActionsComponent} from '@app/catalog/bib-details/catalog-hold-actions.component';
import {BranchAvailabilityExpansionComponent} from '@app/catalog/bib-details/availability/availability-expansion/branch-availability-expansion.component';
import {CombinedAvailabilityBranchNameComponent} from '@app/catalog/bib-details/table-column-components/combined-availability-branch-name.component';
import {MatCard, MatCardContent} from '@angular/material/card';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {SeeMoreLessButtonComponent} from '@lib/components/see-more-less-button/see-more-less-button.component';
import {OrganizationSelectors} from "@store/organization/organization.selectors";
import {NoveListComponent} from "@app/catalog/bib-details/nove-list.component";

@Component({
  selector: 'px-catalog-bib-details',
  templateUrl: './catalog-bib-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CatalogHomeBranchAvailabilityComponent, CatalogHoldActionsComponent, CoverImageComponent, FormatPipe, SelectPipe, TableComponent,
    TruncateDirective, CatalogCarouselCardComponent, MatCard, MatCardContent, TextButtonComponent, SeeMoreLessButtonComponent, NoveListComponent
  ]
})
export class CatalogBibDetailsComponent {
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  private descriptionRef?: TruncateDirective;

  @ViewChild('description', {read: TruncateDirective})
  private set descriptionTruncateDirective(value: TruncateDirective | undefined) {
    this.descriptionRef = value;
    this.changeDetector.markForCheck();
  }

  public readonly selectors = CatalogSelectors.Current;
  public readonly availabilitySelectors = CatalogSelectors.Availability;
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  public readonly patronSelectors = PatronSelectors.Auth;
  public readonly relatedItemsSelectors = CatalogSelectors.RelatedItems;
  public readonly favoriteSelectors = CatalogSelectors.FavoritesIds;
  public readonly viewportSelectors = ViewportSelectors;
  public readonly isPopulatedString = isPopulatedString;
  private readonly store = inject(Store);
  public expandedDescription = false;

  public toggleExpandedDescription(): void {
    this.expandedDescription = !this.expandedDescription;
  }

  public addToFavorites(favorite: CatalogRecord): void {
    if (isPositiveNumber(favorite.id)) {
      this.store.dispatch(CatalogActions.addFavorite(favorite.id));
    }
  }

  public removeFromFavorites(favorite: CatalogRecord): void {
    this.store.dispatch(CatalogActions.showRemoveFavorite(favorite, true));
  }

  public readonly tableDefinition: TableDefinition<CatalogRecordAvailabilityBranch, typeof this.availabilitySelectors> = {
    defaultSelectorContainer: this.availabilitySelectors,
    dataSource: 'branchAvailability',
    responsiveColumnsDefinition: {
      columnNamesSm: ['branchSm', 'copiesSm'],
      columnNamesMd: ['branchSm', 'copiesSm'],
      columnNamesLg: ['branchLg', 'branchAvailableCount', 'copiesLg']
    },
    includePaginationControls: false,
    noDataRowMessage: 'No copies available',
    rowExpansion: {
      rowBased: {
        componentClass: BranchAvailabilityExpansionComponent,
        initiallyExpanded: this.availabilitySelectors.onlyOneBranchAvailableFor
      }
    },
    columns: [
      {
        type: 'component',
        columnName: 'branchLg',
        header: 'Branch',
        componentClass: CombinedAvailabilityBranchNameComponent,
        componentProperties: {
          available: 'branchHasAvailableFor',
          name: 'branchBranchNameFor',
        },
        headerCssClasses: 'w-[33%]',
      },
      {
        type: 'component',
        columnName: 'branchSm',
        header: 'Branch',
        componentClass: CombinedAvailabilityBranchNameComponent,
        componentProperties: {
          available: 'branchHasAvailableFor',
          name: 'branchBranchNameFor',
        },
        headerCssClasses: 'w-full',
      },
      {
        type: 'text',
        value: 'branchAvailableCountFor',
        header: 'Available',
        headerCssClasses: 'w-[33%]',
        truncate: true
      },
      {
        type: 'text',
        value: 'branchOpacVisibleCopiesFor',
        overrideColumnName: 'copiesLg',
        header: 'Copies',
        headerCssClasses: 'w-[33%]',
        truncate: true
      },
      {
        type: 'text',
        value: 'branchOpacVisibleCopiesFor',
        overrideColumnName: 'copiesSm',
        header: 'Copies',
        headerCssClasses: 'w-[5rem] shrink-0',
        truncate: true
      }
    ]
  };

  public get showMoreLessButton(): boolean {
    // If the description is not expanded, but is fully visible, then we don't need a "show more" button
    return this.expandedDescription || !this.descriptionRef?.fullTextIsVisible;
  }
}
